import { createDefaultPipeline } from '../reactFlow/helpers/createDefaultNodesAndEdges'
import { createPlaceholderNode } from '@ui/lib/reactFlow/helpers'
import { useCallback, useEffect, useRef, useState } from 'react'
import { type Edge as ReactFlowEdge, type Node as ReactFlowNode } from 'reactflow'
import { useShallow } from 'zustand/react/shallow'

import { Edge, Node, NodeStep, NodeType, Pipeline } from '@ui/__generated__/graphql'
import { removeTypename } from '@ui/lib/utils'
import { GetNodesAndEdgesLazyQuery, GetNodesAndEdgesWithSourcesLazyQuery } from '@ui/loaders/workflow'
import { FeatureFlagName, useFeatureFlag } from '@ui/providers/FeatureFlagProvider'
import { useNodeAndEdgeStore, useSaveUpdateStatusStore } from '@ui/stores'

const useFetchAndSetupPipeline = ({
  isNew,
  id,
  setupPipelineWorkflow,
  withSteps,
}: {
  isNew: boolean
  id?: string
  setupPipelineWorkflow: (pipeline: Pipeline) => void
  withSteps: boolean
}) => {
  const hasInitialized = useRef(false)
  const [isSettingUpPipeline, setIsSettingUpPipeline] = useState(true)
  const [pipeline, setPipeline] = useState<Pipeline | null>(null)

  const workflowUpdateEnabled = useFeatureFlag(FeatureFlagName.WorkflowUpdate)

  const [getNodesAndEdges] = workflowUpdateEnabled
    ? GetNodesAndEdgesWithSourcesLazyQuery()
    : GetNodesAndEdgesLazyQuery()

  const { setAssignedSourceIds } = useSaveUpdateStatusStore(
    useShallow((state) => ({
      setAssignedSourceIds: state.setAssignedSourceIds,
    }))
  )

  // Fetch and setup the default pipeline workflow
  const fetchAndSetupDefaultPipeline = useCallback(async () => {
    try {
      const pipeline = createDefaultPipeline(withSteps)
      setupPipelineWorkflow(pipeline as Pipeline)
      setPipeline(pipeline as Pipeline)
    } catch (error) {
      console.error('Error setting up pipeline:', error)
    } finally {
      setIsSettingUpPipeline(false)
    }
  }, [setupPipelineWorkflow, withSteps])

  // Fetch and setup an existing pipeline workflow
  const fetchAndSetupExistingPipeline = useCallback(async () => {
    try {
      const { data } = await getNodesAndEdges({ variables: { pipelineId: id as string } })

      if (data?.pipelineWorkflow) {
        setupPipelineWorkflow(data.pipelineWorkflow)
        setPipeline(data.pipelineWorkflow)

        if (workflowUpdateEnabled) {
          setAssignedSourceIds({
            assignedSourceIds: new Set(data.pipelineWorkflow.assignedSources.map((s) => s.id)),
            updatePipelineModified: false,
          })
        }
      }
    } catch (error) {
      console.error('Error setting up pipeline:', error)
    } finally {
      setIsSettingUpPipeline(false)
    }
  }, [getNodesAndEdges, id, setAssignedSourceIds, setupPipelineWorkflow, workflowUpdateEnabled])

  useEffect(() => {
    if (!hasInitialized.current) {
      hasInitialized.current = true

      if (isNew) {
        fetchAndSetupDefaultPipeline()
      } else if (id) {
        fetchAndSetupExistingPipeline()
      }
    }
  }, [isNew, id, hasInitialized, fetchAndSetupDefaultPipeline, fetchAndSetupExistingPipeline])

  return { isSettingUpPipeline, pipeline }
}

export const useSetupNodesEdges = ({ isNew, id, withSteps }: { isNew: boolean; id?: string; withSteps: boolean }) => {
  const { setupNodesAndEdges, setRefreshLayout } = useNodeAndEdgeStore(
    useShallow((state) => ({
      setupNodesAndEdges: state.setupNodesAndEdges,
      setRefreshLayout: state.setRefreshLayout,
    }))
  )

  const { setAssignedSourceIds } = useSaveUpdateStatusStore(
    useShallow((state) => ({
      setAssignedSourceIds: state.setAssignedSourceIds,
    }))
  )

  const setupPipelineWorkflow = useCallback(
    async (pipelineWorkflow: Pipeline, isMinimap?: boolean) => {
      const { nodes, edges, id, name, enabled } = pipelineWorkflow as {
        nodes: Node[]
        edges: Edge[]
        id: string
        name: string
        enabled: boolean
      }

      const sanitizedNodes = nodes.map((node) => {
        const steps = node.data?.steps?.map((step) => removeTypename(step as NodeStep))

        return {
          ...removeTypename(node),
          position: { x: 0, y: 0 },
          data: {
            ...node.data,
            steps,
            isMinimap: isMinimap ?? false,
          },
        }
      }) as ReactFlowNode[]

      const sanitizedEdges = edges.map((edge) => removeTypename(edge)) as ReactFlowEdge[]

      setupNodesAndEdges({
        nodes: sanitizedNodes,
        edges: sanitizedEdges,
        loafId: id,
        loafName: name,
        loafState: enabled,
      })

      // We only want to create placeholder nodes when we are not in the mini map
      if (!isMinimap) {
        for (const node of sanitizedNodes) {
          if (!sanitizedEdges.some((edge) => edge.source === node.id) && node.type !== NodeType.Export) {
            await createPlaceholderNode({ currentNodeId: node.id })
          }
        }
      }

      setAssignedSourceIds({
        assignedSourceIds: new Set(pipelineWorkflow.assignedSources?.map((source) => source.id)),
        updatePipelineModified: false,
      })

      setRefreshLayout(true)
    },
    [setupNodesAndEdges, setAssignedSourceIds, setRefreshLayout]
  )

  const { isSettingUpPipeline, pipeline } = useFetchAndSetupPipeline({
    isNew,
    id,
    setupPipelineWorkflow,
    withSteps,
  })

  return { isSettingUpPipeline, setupPipelineWorkflow, pipeline }
}
